import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Sparkles, Users,
  ArrowRight, Check,
  Calculator, Wrench, Truck, Hammer, FileText, Package
} from 'lucide-react';
import RemoddashLogo from '../components/RemoddashLogo';
import NeedHelpStickyBar from '../components/NeedHelpStickyBar';
import PortalSwitcher from '../components/PortalSwitcher';

const LandingPage = () => {
  const navigate = useNavigate();
  // Presidential dark blue is the only theme on the landing page. No toggle, no chooser.
  const isDark = true;

  // Footer-link deep-link support: when the gateway footer (or anywhere else)
  // sends the user to /landing#services or /landing#about we scroll the
  // anchored section into view after React has had a chance to mount it.
  // No-op if there's no hash. Pure side-effect — no UI change.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (!hash) return;
    // Two animation frames to let the section render before we measure it.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          const el = document.querySelector(hash);
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } catch (_e) { /* invalid selector — ignore */ }
      });
    });
  }, []);

  const roles = [
    "Office Manager", "Bookkeeper", "Financial Operator", "Dispatcher",
    "Customer Support", "Sales Assistant", "Estimator", "Pricing Analyst",
    "Accounts Receivable", "Accounts Payable", "Follow-Up Specialist",
    "Operations Coordinator", "Business Analyst",
  ];

  const industries = [
    { title: "Home Services", subtitle: "HVAC · Plumbing · Electrical · Appliance Repair" },
    { title: "Property Maintenance & Facilities", subtitle: null },
    { title: "Field & Contractor Operations", subtitle: null },
    { title: "Delivery & Logistics", subtitle: null },
    { title: "Medical & Courier Services", subtitle: null },
    { title: "Mobile Service Professionals", subtitle: null },
    { title: "General Local Services", subtitle: null },
  ];

  const uniqueFeatures = [
    { icon: <Users className="w-10 h-10" />, title: "RemodConnect", subtitle: "Customer-to-Provider Routing", description: "Customer-to-provider routing, matching, distribution, and connected service workflows — powered by Ari." },
    { icon: <Calculator className="w-10 h-10" />, title: "RemodPay", subtitle: "Financial Operating System", description: "A complete financial operating system: payments, distributions, reserves, forecasting, contractor payouts, and financial intelligence." },
    { icon: <Package className="w-10 h-10" />, title: "RemodInventory", subtitle: "Equipment & Parts Intelligence", description: "Equipment intelligence, placard intelligence, parts intelligence, truck inventory, warehouse inventory, and field-connected inventory workflows." },
    { icon: <FileText className="w-10 h-10" />, title: "RemodWarranty", subtitle: "Warranty Operations", description: "Warranty intelligence, documentation, equipment tracking, claim workflows, labor-recovery workflows, and warranty operations." },
    { icon: <Sparkles className="w-10 h-10" />, title: "Workspace Evolution", subtitle: "The Platform Adapts to You", description: "Ari learns how each company operates, configures the workspace around the business, recommends workflows and forms, adapts processes, and evolves the workspace as the company grows." },
  ];

  const VeteranBadge = ({ size = 60, idPrefix = "badge" }) => (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'}}>
      <defs>
        <linearGradient id={`${idPrefix}-silverRing`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFFFFF" /><stop offset="30%" stopColor="#E5E5E5" />
          <stop offset="50%" stopColor="#A8A8A8" /><stop offset="70%" stopColor="#E5E5E5" /><stop offset="100%" stopColor="#FFFFFF" />
        </linearGradient>
        <linearGradient id={`${idPrefix}-blueBadge`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1E40AF" /><stop offset="50%" stopColor="#1E3A8A" /><stop offset="100%" stopColor="#172554" />
        </linearGradient>
        <clipPath id={`${idPrefix}-innerCircle`}><circle cx="50" cy="50" r="38" /></clipPath>
      </defs>
      <circle cx="50" cy="50" r="48" fill={`url(#${idPrefix}-silverRing)`} stroke="#9CA3AF" strokeWidth="1" />
      <circle cx="50" cy="50" r="42" fill="none" stroke="#B0B0B0" strokeWidth="2" />
      <circle cx="50" cy="50" r="38" fill={`url(#${idPrefix}-blueBadge)`} />
      <g clipPath={`url(#${idPrefix}-innerCircle)`} opacity="0.4">
        <rect x="12" y="26" width="76" height="5" fill="#EF4444" />
        <rect x="12" y="36" width="76" height="5" fill="#EF4444" />
        <rect x="12" y="46" width="76" height="5" fill="#EF4444" />
        <rect x="12" y="56" width="76" height="5" fill="#EF4444" />
        <rect x="12" y="66" width="76" height="5" fill="#EF4444" />
        <rect x="12" y="26" width="32" height="25" fill="#3B82F6" />
      </g>
      <circle cx="50" cy="50" r="34" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
      <g fill="white" fontSize="6">
        <text x="30" y="33" textAnchor="middle">&#9733;</text><text x="38" y="32" textAnchor="middle">&#9733;</text>
        <text x="46" y="31" textAnchor="middle">&#9733;</text><text x="54" y="31" textAnchor="middle">&#9733;</text>
        <text x="62" y="32" textAnchor="middle">&#9733;</text><text x="70" y="33" textAnchor="middle">&#9733;</text>
      </g>
      <text x="50" y="50" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial Black, Arial" letterSpacing="0.5">VETERAN</text>
      <text x="50" y="62" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial Black, Arial" letterSpacing="0.5">OWNED</text>
      <g fill="white" fontSize="6">
        <text x="30" y="75" textAnchor="middle">&#9733;</text><text x="38" y="76" textAnchor="middle">&#9733;</text>
        <text x="46" y="77" textAnchor="middle">&#9733;</text><text x="54" y="77" textAnchor="middle">&#9733;</text>
        <text x="62" y="76" textAnchor="middle">&#9733;</text><text x="70" y="75" textAnchor="middle">&#9733;</text>
      </g>
    </svg>
  );

  // ── THEME COLORS ──
  const bg = isDark
    ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950'
    : 'bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100';
  const headerBg = isDark ? 'bg-slate-950/80 border-slate-800' : 'bg-slate-100/80 border-slate-300';
  const heroText = isDark ? 'text-white' : 'text-slate-900';
  const heroSub = isDark ? 'text-slate-400' : 'text-slate-500';
  const heroBody = isDark ? 'text-slate-300' : 'text-slate-600';
  const cardBg = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-200/60 border-slate-300 shadow-sm';
  const cardHover = isDark ? 'hover:border-blue-500/40' : 'hover:border-blue-400 hover:shadow-md';
  const cardText = isDark ? 'text-white' : 'text-slate-900';
  const cardSub = isDark ? 'text-slate-400' : 'text-slate-500';
  const sectionBg2 = isDark ? 'bg-[radial-gradient(ellipse_at_center,_#3b0764_0%,_#4c1d95_25%,_#3b0764_55%,_#2a1065_72%,_#172554_92%,_#0a1230_100%)]' : 'bg-gradient-to-br from-purple-50 via-violet-50 to-white';
  const sectionBg3 = isDark ? 'bg-slate-900/60' : 'bg-slate-100/80';
  const sectionBg5 = isDark ? 'bg-slate-900/50' : 'bg-slate-100/60';
  const footerBorder = isDark ? 'border-slate-800' : 'border-slate-300';
  const savingsBg = isDark ? 'from-green-600/20 to-emerald-600/20 border-green-500/30' : 'from-green-50 to-emerald-50 border-green-300';
  const purpleText = isDark ? 'text-purple-200/80' : 'text-purple-700';
  const tradeBg = isDark ? 'bg-slate-800/40 border-slate-700' : 'bg-slate-200/60 border-slate-300 shadow-sm';
  const blurOrb = isDark ? 'bg-blue-600/20' : 'bg-blue-200/40';

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-700 relative`} data-testid="landing-page">
      {/* Floating top-center: blue "Re" in white rounded container with lighter-blue glow. No bar, no border. */}
      <div
        className="absolute top-4 left-1/2 -translate-x-1/2 z-50 w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-white flex items-center justify-center"
        style={{ boxShadow: '0 0 28px 4px rgba(96, 165, 250, 0.75)' }}
        data-testid="landing-header-re"
      >
        <img src="/remodify-logo-blue.png" alt="RE" className="h-7 sm:h-9 w-auto object-contain" />
      </div>

      {/* Floating top-right: portal switcher (cycles Customer ⇄ Tools).
          Replaces the previous "dead-end" mini-customer card per user
          direction (Feb 2026): users should NEVER need to return to the
          gateway — they hop between portals from any portal. */}
      <div className="absolute top-4 right-4 z-50">
        <PortalSwitcher currentPortal="provider" />
      </div>

      {/* ═══ MAIN LANDING CONTENT — begins immediately at the hero ═══ */}
      <div>
        {/* HERO */}
        <section className="pt-32 pb-16 sm:pt-36 sm:pb-20 px-5 sm:px-8 relative overflow-hidden">
            <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] ${blurOrb} rounded-full blur-3xl transition-colors duration-700`} />
            <div className="w-full text-center relative z-10">
              <h2 className={`text-[42px] sm:text-5xl md:text-6xl lg:text-7xl font-bold ${heroText} mb-6 leading-[1.1] transition-colors duration-500`}>
                You Didn't Start Your Business
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500">To Drown In Paperwork.</span>
              </h2>
              <p className={`text-xl ${heroSub} mb-6 italic transition-colors duration-500`}>That's why we built Remoddash.</p>
              <p className={`text-lg sm:text-xl ${heroBody} mb-8 max-w-3xl mx-auto transition-colors duration-500`}>
                Remoddash runs the office so you can stay focused on the work that pays.
                Scheduling, quotes, invoices, follow-ups, and customer communication
                handled automatically from one intelligent platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/signup')}
                  size="lg"
                  style={{ backgroundImage: 'linear-gradient(to right, #2563EB 0%, #1D4ED8 50%, #1E40AF 100%)', boxShadow: '0 6px 14px rgba(37, 99, 235, 0.3)' }}
                  className="text-white text-lg px-8 py-6 font-semibold hover:brightness-95"
                  data-testid="hero-cta-button"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  onClick={() => navigate('/login')}
                  variant="outline"
                  size="lg"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', color: '#1E293B', boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)' }}
                  className="text-lg px-8 py-6 font-semibold rounded-xl hover:brightness-95"
                  data-testid="hero-login-btn"
                >
                  I Already Have One
                </Button>
              </div>
              <div className={`flex flex-wrap justify-center gap-x-6 gap-y-2 mt-8 ${heroSub} text-sm`}>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-500" />No credit card required</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-500" />Setup in under 10 minutes</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-500" />Cancel anytime</span>
              </div>
            </div>
          </section>

          {/* MEET ARI */}
          <section id="about" className={`py-16 sm:py-20 px-4 ${sectionBg2} transition-colors duration-500`}>
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col items-center text-center mb-10">
                <div className={`${isDark ? 'bg-purple-600/30 border-purple-500/40' : 'bg-purple-100 border-purple-200'} border p-4 rounded-2xl mb-6 transition-colors duration-500`}>
                  <Sparkles className={`w-10 h-10 ${isDark ? 'text-purple-300' : 'text-purple-600'}`} />
                </div>
                <h3 className={`text-3xl sm:text-4xl md:text-5xl font-bold ${heroText} mb-4 transition-colors duration-500`}>
                  Meet <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-300">Ari</span> — The Operating Layer of RemodDash.
                </h3>
              </div>
              <div className="max-w-3xl mx-auto space-y-6 text-center">
                <p className={`text-lg ${purpleText} transition-colors duration-500`}>Ari isn't a chatbot, an assistant, or a support feature. Ari is the intelligence and operating layer of RemodDash.</p>
                <p className={`text-lg ${heroBody} transition-colors duration-500`}>Ari helps manage operations, dispatch, and customer communication — handling estimates, invoicing, and collections, and delivering financial intelligence, reporting, workflow automation, approvals, and business growth.</p>
                <p className={`text-lg ${heroBody} transition-colors duration-500`}>She works across every layer of the platform: the Command Center, Autopilot, Workspace Evolution, Financial Intelligence, Field Intelligence, and Business Intelligence.</p>
                <p className={`text-lg ${heroBody} transition-colors duration-500`}>Wherever work happens in RemodDash, Ari is operating it — learning your business and growing with it.</p>
              </div>
              <div className="mt-12 text-center">
                <p className={`text-xl sm:text-2xl font-semibold ${isDark ? 'text-purple-300' : 'text-purple-600'} transition-colors duration-500`}>Here's what Ari replaces.</p>
              </div>
            </div>
          </section>

          {/* ROLES REPLACED */}
          <section className={`py-16 sm:py-20 px-4 ${sectionBg3} transition-colors duration-500`}>
            <div className="max-w-5xl mx-auto">
              <h3 className={`text-3xl sm:text-4xl md:text-5xl font-bold ${heroText} text-center mb-10 transition-colors duration-500`}>
                One Platform. <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">13 Roles Replaced.</span>
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto mb-10">
                {roles.map((role, i) => (
                  <Card key={i} className={`${cardBg} ${cardHover} transition-all`}>
                    <CardContent className="p-4 text-center">
                      <h4 className={`${cardText} font-semibold text-sm transition-colors duration-500`}>{role}</h4>
                      <p className="text-green-400 text-xs font-bold mt-1">Replaced</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="text-center space-y-4">
                <p className={`${heroSub} text-lg transition-colors duration-500`}>Stop paying salaries for an entire office team.</p>
                <p className={`${heroBody} text-lg font-medium transition-colors duration-500`}>Remoddash AI handles the work and you keep the profits.</p>
                <div className={`inline-block bg-gradient-to-r ${savingsBg} border rounded-2xl px-10 py-6 mt-6 transition-colors duration-500`}>
                  <p className="text-green-400 text-sm font-medium mb-1">Your Annual Savings</p>
                  <p className={`${heroText} text-5xl sm:text-6xl font-bold transition-colors duration-500`}>
                    $703,000<span className={`text-lg ${heroSub} font-normal`}> / year</span>
                  </p>
                </div>
                <p className={`${heroSub} text-lg mt-8 pt-4 transition-colors duration-500`}>These systems are what make it possible.</p>
              </div>
            </div>
          </section>

          {/* INDUSTRIES WE SERVE — provider-facing display, not an anchor target anymore */}
          <section className={`py-16 sm:py-20 px-4 ${sectionBg5} transition-colors duration-500`}>
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h3 className={`text-3xl sm:text-4xl md:text-5xl font-bold ${heroText} mb-4 transition-colors duration-500`}>
                  One Platform. <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Infinite Workspaces.</span>
                </h3>
                <p className={`${heroBody} text-lg max-w-3xl mx-auto transition-colors duration-500`}>
                  RemodDash is the AI Business Operating System powered by Ari. Every company runs on the same platform, while Ari builds and evolves a workspace tailored to the way that business operates. Industries aren't separate products — they're simply different configurations of the same platform.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 max-w-4xl mx-auto">
                {industries.map((industry, i) => (
                  <Card key={i} className={`${tradeBg} ${cardHover} transition-all`}>
                    <CardContent className="p-4 text-center">
                      <h4 className={`${cardText} font-semibold text-base transition-colors duration-500`}>{industry.title}</h4>
                      {industry.subtitle && (
                        <p className={`${cardSub} text-xs mt-1.5 transition-colors duration-500`}>{industry.subtitle}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className={`${heroSub} text-center text-base italic mt-8 transition-colors duration-500`}>
                …and many more.
              </p>
            </div>
          </section>

          {/* UNIQUE FEATURES */}
          <section className="py-16 sm:py-20 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h3 className={`text-3xl sm:text-4xl md:text-5xl font-bold ${heroText} mb-4 transition-colors duration-500`}>
                  What Exists Because of <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Ari</span>
                </h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {uniqueFeatures.map((f, i) => (
                  <Card key={i} className={`${cardBg} ${cardHover} transition-all`}>
                    <CardContent className="p-6">
                      <div className={`${isDark ? 'text-blue-400' : 'text-blue-600'} mb-4 transition-colors duration-500`}>{f.icon}</div>
                      <h4 className={`${cardText} font-bold text-lg transition-colors duration-500`}>{f.title}</h4>
                      <p className="text-orange-400 text-sm font-medium mb-3">{f.subtitle}</p>
                      <p className={`${cardSub} text-sm leading-relaxed transition-colors duration-500`}>{f.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="max-w-3xl mx-auto text-center space-y-4">
                <p className={`${heroBody} text-lg transition-colors duration-500`}>These are the systems Ari powers across the platform — each one connected, intelligent, and working together.</p>
              </div>
              <div className="mt-16 max-w-5xl mx-auto">
                <h4 className={`text-2xl sm:text-3xl font-bold ${heroText} text-center mb-4 transition-colors duration-500`}>
                  The platform adapts to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">the business.</span>
                </h4>
                <p className={`${heroSub} text-lg text-center mb-10 transition-colors duration-500`}>The business does not adapt to the platform.</p>
                <div className="grid sm:grid-cols-3 gap-6">
                  <div className={`${tradeBg} rounded-2xl p-6 transition-colors duration-500`}>
                    <Wrench className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'} mb-3`} />
                    <h5 className={`${cardText} font-semibold mb-2 transition-colors duration-500`}>Appliance Repair?</h5>
                    <p className={`${cardSub} text-sm transition-colors duration-500`}>AI learns your brands, common repairs, parts suppliers, and pricing for refrigerators, washers, dryers, ovens, and dishwashers.</p>
                  </div>
                  <div className={`${tradeBg} rounded-2xl p-6 transition-colors duration-500`}>
                    <Truck className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'} mb-3`} />
                    <h5 className={`${cardText} font-semibold mb-2 transition-colors duration-500`}>Medical Courier?</h5>
                    <p className={`${cardSub} text-sm transition-colors duration-500`}>AI tracks delivery routes, compliance requirements, scheduling patterns, and customer coordination for time sensitive logistics.</p>
                  </div>
                  <div className={`${tradeBg} rounded-2xl p-6 transition-colors duration-500`}>
                    <Hammer className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'} mb-3`} />
                    <h5 className={`${cardText} font-semibold mb-2 transition-colors duration-500`}>Any Service Business?</h5>
                    <p className={`${cardSub} text-sm transition-colors duration-500`}>Pool service, locksmith, pest control, photography, and beyond. Ari learns your business-specific patterns, pricing, and supplier behavior.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FINAL CLOSE — Ari's voice */}
          <section className={`py-20 sm:py-24 px-4 ${sectionBg5} transition-colors duration-500`}>
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isDark ? 'bg-purple-600/20 border border-purple-500/30' : 'bg-purple-100 border border-purple-200'}`}>
                  <Sparkles className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
              </div>
              <h3 className={`text-2xl sm:text-3xl md:text-4xl font-bold ${heroText} mb-4 transition-colors duration-500`}>Now that you've seen what I can do —</h3>
              <p className={`text-xl sm:text-2xl font-medium ${isDark ? 'text-purple-300' : 'text-purple-600'} mb-10 transition-colors duration-500`}>let's get you set up.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/signup')}
                  size="lg"
                  style={{ backgroundImage: 'linear-gradient(to right, #2563EB 0%, #1D4ED8 50%, #1E40AF 100%)', boxShadow: '0 6px 14px rgba(37, 99, 235, 0.3)' }}
                  className="text-white text-lg px-10 py-6 font-semibold hover:brightness-95"
                  data-testid="final-cta-button"
                >
                  Create Your Account
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  onClick={() => navigate('/login')}
                  variant="outline"
                  size="lg"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', color: '#1E293B', boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)' }}
                  className="text-lg px-8 py-6 font-semibold rounded-xl hover:brightness-95"
                  data-testid="final-login-btn"
                >
                  I Already Have One
                </Button>
              </div>
              <p className={`${heroSub} text-sm mt-6 transition-colors duration-500`}>No credit card required. Set up in minutes.</p>
            </div>
          </section>

          {/* Footer — logo removed per spec. Copyright line only. */}
          <footer className={`py-8 px-4 pb-20 border-t ${footerBorder} transition-colors duration-500`}>
            <div className="max-w-6xl mx-auto flex items-center justify-center">
              <p className={`${heroSub} text-sm transition-colors duration-500`}>
                {new Date().getFullYear()} RemodWorks. All rights reserved.
              </p>
            </div>
          </footer>
        </div>

        {/* Sticky "Need help?" bar — fixed to the bottom of the viewport on the landing page only.
            Email is the single contact channel surfaced here per product direction. */}
        <NeedHelpStickyBar variant="blue" testId="landing-need-help-bar" />
    </div>
  );
};

export default LandingPage;
